<template>
  <vue-final-modal
    :value="dialogVisible"
    name="yagibuddy-popup-dialog"
    :classes="['yagibuddy-dialog__container']"
    :content-class="['yagibuddy-dialog__content', 'v--modal-box', 'v--modal']"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
    @closed="onModalClosed"
  >
    <article class="yagibuddy-dialog">
      <span class="yagibuddy-dialog__pr">{{ copy.prBadge }}</span>
      <button
        type="button"
        class="yagibuddy-dialog__close"
        :aria-label="copy.closeAriaLabel"
        @click="closeDialog"
      >
        ×
      </button>
      <img
        class="yagibuddy-dialog__goat"
        src="/static/img/yagibuddy/goat.png"
        alt=""
        aria-hidden="true"
      />
      <div class="yagibuddy-dialog__intro">
        <p class="yagibuddy-dialog__title">
          <span class="yagibuddy-dialog__title-prefix">{{
            copy.titlePrefix
          }}</span>
          <img
            class="yagibuddy-dialog__logo"
            src="/static/img/yagibuddy/logo.png"
            alt="yagibuddy"
          />
        </p>
        <p class="yagibuddy-dialog__complete">{{ copy.downloadComplete }}</p>
        <p class="yagibuddy-dialog__subtitle">{{ copy.subtitle }}</p>
      </div>
      <ul class="yagibuddy-dialog__features">
        <li
          v-for="feature in copy.features"
          :key="feature.id"
          class="yagibuddy-dialog__feature"
        >
          <img
            class="yagibuddy-dialog__check"
            src="/static/img/yagibuddy/check.svg"
            alt=""
            aria-hidden="true"
          />
          <span
            ><template v-if="feature.restPrefix">{{
              feature.restPrefix
            }}</template
            ><strong>{{ feature.emphasis }}</strong
            ><template v-if="feature.rest">{{ feature.rest }}</template></span
          >
        </li>
      </ul>
      <p class="yagibuddy-dialog__consent">
        {{ copy.consentPrefix
        }}<a
          class="yagibuddy-dialog__privacy"
          :href="copy.privacyUrl"
          target="_blank"
          rel="noopener noreferrer"
          >{{ copy.privacyLabel }}</a
        ><template v-if="showPrivacyPolicyLink"
          >・<a
            class="yagibuddy-dialog__privacy"
            :href="copy.privacyUrl"
            target="_blank"
            rel="noopener noreferrer"
            >{{ copy.privacyPolicyLabel }}</a
          ></template
        >{{ copy.consentSuffix }}
      </p>
      <button type="button" class="yagibuddy-dialog__cta" @click="onCtaClick">
        {{ copy.ctaLabel }}
      </button>
    </article>
  </vue-final-modal>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import {
  emitter,
  FORCE_HIDE_RT_DIALOGS,
  YAGIBUDDY_DIALOG_CLOSED
} from '@/plugins/emitter'
import fallbackJson from '@/assets/realtime-transfer/yagibuddy-dialog.json'
import {
  mergeYagibuddyCopy,
  type YagibuddyCopy
} from '@/utils/realtime-transfer/yagibuddyCopy'

const OPEN_YAGIBUDDY_POPUP_DIALOG = 'open-yagibuddy-popup-dialog'
const fallback = fallbackJson as YagibuddyCopy
const dialogVisible = ref(false)
const copy = ref<YagibuddyCopy>(fallback)
const ctaUrl = ref(fallback.ctaUrlFallback)
const showPrivacyPolicyLink = computed(() => {
  const label = copy.value.privacyPolicyLabel
  return !!label && !copy.value.privacyLabel.includes('プライバシーポリシー')
})

function onOpen(argList: unknown) {
  const args = Array.isArray(argList) ? argList : []
  const merged = mergeYagibuddyCopy(args[3], fallback)
  copy.value = merged.copy
  ctaUrl.value = merged.ctaUrl
  dialogVisible.value = true
}

function forceHide() {
  dialogVisible.value = false
}

function onVisibleInput(next: unknown) {
  dialogVisible.value = next === true
}

function closeDialog() {
  dialogVisible.value = false
}

function onModalClosed() {
  emitter.emit(YAGIBUDDY_DIALOG_CLOSED)
}

function onCtaClick() {
  window.open(ctaUrl.value, '_blank', 'noopener,noreferrer')
  closeDialog()
}

onBeforeMount(() => {
  emitter.on(OPEN_YAGIBUDDY_POPUP_DIALOG, onOpen)
  emitter.on(FORCE_HIDE_RT_DIALOGS, forceHide)
})
onBeforeUnmount(() => {
  emitter.off(OPEN_YAGIBUDDY_POPUP_DIALOG, onOpen)
  emitter.off(FORCE_HIDE_RT_DIALOGS, forceHide)
})
</script>

<style scoped lang="scss">
.yagibuddy-dialog {
  position: relative;
  box-sizing: border-box;
  width: min(calc(100vw - 1.5rem), 31.5rem);
  padding: 2.55rem 10% 1.35rem;
  overflow: hidden;
  color: #404040;
  background: #fff;
  border-radius: 1.4rem;
  box-shadow: 0 18px 44px rgb(0 0 0 / 18%);
}

.yagibuddy-dialog__pr {
  position: absolute;
  top: 1.15rem;
  left: 1.2rem;
  display: inline-block;
  padding: 0.1rem 0.4rem;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #888;
  background: transparent;
  border: 1px solid #dfdfdf;
  border-radius: 0.2rem;
}

.yagibuddy-dialog__close {
  position: absolute;
  top: 0.5rem;
  right: 0.85rem;
  z-index: 4;
  padding: 0;
  font-size: 1.85rem;
  font-weight: 400;
  line-height: 1;
  color: #e3165c;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.yagibuddy-dialog__goat {
  position: absolute;
  top: 3.15rem;
  right: -0.45rem;
  z-index: 3;
  width: 8.6rem;
  pointer-events: none;
}

.yagibuddy-dialog__intro {
  margin-bottom: 0.95rem;
  text-align: center;
}

.yagibuddy-dialog__title {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.2rem;
}

.yagibuddy-dialog__title-prefix {
  font-size: 1.12rem;
  font-weight: 700;
  color: #404040;
  white-space: nowrap;
}

.yagibuddy-dialog__logo {
  display: block;
  width: auto;
  height: 2.95rem;
}

.yagibuddy-dialog__complete {
  margin: 0 0 0.55rem;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #404040;
}

.yagibuddy-dialog__subtitle {
  display: inline-block;
  margin: 0;
  padding: 0.32rem 0.95rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: #449dae;
  background: #f2fafc;
  border: 1px solid #b1d7df;
  border-radius: 999px;
}

.yagibuddy-dialog__features {
  position: relative;
  z-index: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  text-align: left;
}

.yagibuddy-dialog__feature {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.7rem;
  padding: 0.72rem 0.9rem;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.35;
  color: #404040;
  background: #f2fafc;
  border-radius: 0.7rem;
}

.yagibuddy-dialog__check {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
}

.yagibuddy-dialog__feature strong {
  font-weight: 800;
  color: #449dae;
}

.yagibuddy-dialog__consent {
  position: relative;
  z-index: 2;
  margin: 0.75rem 0 0.9rem;
  padding: 0.7rem 0.85rem;
  font-size: 0.7rem;
  line-height: 1.55;
  color: #555;
  text-align: left;
  background: #fff;
  border: 1px dashed #cfcfcf;
  border-radius: 0.45rem;
}

.yagibuddy-dialog__privacy {
  color: #555;
  text-decoration: underline;
}

.yagibuddy-dialog__cta {
  position: relative;
  z-index: 2;
  display: block;
  width: 100%;
  padding: 0.95rem 0.5rem;
  font-size: 1rem;
  font-weight: 800;
  color: #fff;
  background: #e3165c;
  border: 0;
  border-radius: 0.75rem;
  cursor: pointer;
}
</style>

<style>
.yagibuddy-dialog__container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.yagibuddy-dialog__content.v--modal-box.v--modal {
  width: auto !important;
  padding: 0;
  background: transparent;
  box-shadow: none;
}
</style>
